import type { ReactNode } from "react";
import { useSession } from "./session";
import { Link, type Route } from "./router";
import { Icon } from "./Icon";
import { IconButton } from "./primitives";

/**
 * The app frame: a top app bar + a bottom nav on mobile / rail-style tabs on
 * desktop. Everything routes through the History-API router.
 */

const NAV: { route: Route; label: string; icon: string }[] = [
  { route: { name: "catalog" }, label: "الأدوات", icon: "grid_view" },
  { route: { name: "history" }, label: "تاريخي", icon: "history" },
  { route: { name: "coverage" }, label: "التغطية", icon: "fact_check" },
];

function routeKey(r: Route): string {
  return r.name;
}

export function AppShell({
  route,
  navigate,
  children,
}: {
  route: Route;
  navigate: (r: Route) => void;
  children: ReactNode;
}) {
  const { user, quota, openAuth, signOut } = useSession();
  const active = routeKey(route);

  return (
    <div className="min-h-dvh bg-surface pb-20 sm:pb-0">
      {/* ─── Top app bar ─── */}
      <header className="sticky top-0 z-30 border-b border-outline-variant bg-surface/90 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-5xl items-center gap-3 px-4 sm:px-6">
          <Link
            to={{ name: "catalog" }}
            navigate={navigate}
            className="md-state flex items-center gap-2 rounded-full py-1 pe-3 ps-1"
            ariaLabel="كلشي — الصفحة الرئيسية"
          >
            <span className="grid h-9 w-9 place-items-center rounded-lg bg-primary text-on-primary shadow-e1">
              <Icon name="apps" size={22} filled />
            </span>
            <span className="md-title-large text-on-surface">كلشي</span>
          </Link>

          {/* Desktop tabs */}
          <nav className="mx-auto hidden items-center gap-1 sm:flex" aria-label="التنقل الرئيسي">
            {NAV.map((item) => {
              const isActive = active === routeKey(item.route);
              return (
                <Link
                  key={item.label}
                  to={item.route}
                  navigate={navigate}
                  className={[
                    "md-state md-label-large flex items-center gap-2 rounded-full px-4 py-2",
                    isActive
                      ? "bg-secondary-container text-on-secondary-container"
                      : "text-on-surface-variant",
                  ].join(" ")}
                >
                  <Icon name={item.icon} size={20} filled={isActive} />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="ms-auto flex items-center gap-2 sm:ms-0">
            {typeof quota?.remaining === "number" ? (
              <span
                className="md-label-medium hidden items-center gap-1 rounded-full bg-surface-c px-3 py-1.5 text-on-surface-variant xs:flex"
                title="الوحدات المتبقية اليوم"
              >
                <Icon name="bolt" size={16} className="text-tertiary" filled />
                <bdi>{quota.remaining}</bdi>
              </span>
            ) : null}

            {user ? (
              <div className="flex items-center gap-1">
                <span className="hidden text-end sm:block">
                  <span className="md-label-medium block text-on-surface-variant">مسجّل</span>
                  <bdi className="md-body-small block text-on-surface-variant">{user.phone}</bdi>
                </span>
                <IconButton icon="logout" label="تسجيل خروج" onClick={() => void signOut()} />
              </div>
            ) : (
              <button
                onClick={() => openAuth()}
                className="md-state md-label-large flex items-center gap-1.5 rounded-full border border-outline px-4 py-2 text-primary"
              >
                <Icon name="login" size={18} />
                دخول
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8">{children}</main>

      {/* ─── Bottom nav (mobile) ─── */}
      <nav
        className="fixed inset-x-0 bottom-0 z-30 border-t border-outline-variant bg-surface-c/95 backdrop-blur-md sm:hidden"
        aria-label="التنقل الرئيسي"
      >
        <div className="mx-auto grid max-w-md grid-cols-3">
          {NAV.map((item) => {
            const isActive = active === routeKey(item.route);
            return (
              <Link
                key={item.label}
                to={item.route}
                navigate={navigate}
                className="md-state flex flex-col items-center gap-1 py-2.5"
                ariaLabel={item.label}
              >
                <span
                  className={[
                    "grid h-8 w-16 place-items-center rounded-full transition-colors",
                    isActive ? "bg-secondary-container text-on-secondary-container" : "text-on-surface-variant",
                  ].join(" ")}
                >
                  <Icon name={item.icon} size={22} filled={isActive} />
                </span>
                <span
                  className={`md-label-medium ${isActive ? "text-on-surface" : "text-on-surface-variant"}`}
                >
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
